/**
 * Schema Zod da Tabela de Preços completa.
 * Espelha os types em ../types/tabela-precos.ts — manter sincronizado.
 */

import { z } from 'zod';

import { emailSchema, isoTimestampSchema } from './common.js';

const rsSchema = z.number().nonnegative().finite();
const pctSchema = z.number().min(0).max(100);

// ─── Venda ───────────────────────────────────────────────────────────────

const precosEssencialSchema = z.object({
  dispositivo: rsSchema,
  servidor: rsSchema,
  horaSuporte: rsSchema,
  horaAdicional: rsSchema,
  usuarioM365: rsSchema.default(0),
});

const precosProfessionalSchema = z.object({
  usuario: rsSchema,
  servidorComEdr: rsSchema,
  usuarioM365: rsSchema.default(0),
});

const precosPremiumSchema = z.object({
  equipeInfra3h: rsSchema,
  gestorMensal: rsSchema,
});

const precosEmailImapSchema = z.object({
  '5gb': rsSchema,
  '25gb': rsSchema,
  '50gb': rsSchema,
  '100gb': rsSchema,
  '200gb': rsSchema,
});

const precosEmailExchangeSchema = z.object({
  '50gb': rsSchema,
  '100gb': rsSchema,
  '200gb': rsSchema,
});

const precosEmailExtrasSchema = z.object({
  smtp2k: rsSchema,
  smtp5k: rsSchema,
  smtp10k: rsSchema,
  smtp50k: rsSchema,
  auditoria30d: rsSchema,
  ativacaoMigracao: rsSchema,
  infoBox25gb: rsSchema,
  infoBox50gb: rsSchema,
  infoBox100gb: rsSchema,
  backup60d: rsSchema,
});

const precosBackupNuvemSchema = z.object({
  documents: rsSchema,
  m365: rsSchema,
  perGb: rsSchema,
  estacao500gb: rsSchema,
  servidorFisico2tb: rsSchema,
  servidorVirtual2tb: rsSchema,
  testeRestore: rsSchema,
  // default(0) pra retrocompat: tabelas gravadas antes do DRaaS carregam sem quebrar.
  draas: rsSchema.default(0),
});

const precosRedeWirelessSchema = z.object({
  ap6Ghz: rsSchema,
  apLongoAlcance: rsSchema,
  apExterno: rsSchema,
  switch8pGiga: rsSchema,
  switch8pPoe: rsSchema,
  switch24pGiga: rsSchema,
  switch24pPoe: rsSchema,
  switch48pGiga: rsSchema,
  switch48pPoe: rsSchema,
});

const precosFirewallSchema = z.object({
  mikrotik5p: rsSchema,
  mikrotikWireless: rsSchema,
  gestaoFirewallCliente: rsSchema,
});

const precosSegurancaSchema = z.object({
  antivirus: rsSchema,
  edr: rsSchema,
});

const precosDispositivosSchema = z.object({
  impressora: rsSchema,
  nobreak: rsSchema,
  firewall: rsSchema,
  roteadorAp: rsSchema,
  switch: rsSchema,
  // Campos novos — default 0 pra compat com docs antigos do Cosmos
  dvr: rsSchema.default(0),
  storage: rsSchema.default(0),
  outros: rsSchema.default(0),
});

const precosOnboardingSchema = z.object({
  // Legados (mantidos para retrocompat)
  sem: rsSchema,
  express: rsSchema,
  standard: rsSchema,
  enterprise: rsSchema,
  // Novo modelo dimensional
  baseFixo: rsSchema,
  valorPorEstacao: rsSchema,
  valorPorServidor: rsSchema,
  valorPorFirewall: rsSchema,
  valorPorDispositivoRede: rsSchema,
});

// ─── HaaS (Hardware as a Service) ───────────────────────────────────────
const precosHaasItemSchema = z.object({
  modeloPadrao: z.string().min(1).max(200),
  quantidadePadrao: z.number().int().min(0).max(9999).default(1),
  valorMensalPadrao: rsSchema,
  precoVendaPadrao: rsSchema,
  pctHardwarePadrao: z.number().int().min(0).max(100),
});

const precosHaasDefault = {
  mikrotik: {
    modeloPadrao: 'Mikrotik RB5009UPr+S+IN',
    quantidadePadrao: 1,
    valorMensalPadrao: 219,
    precoVendaPadrao: 3500,
    pctHardwarePadrao: 70,
  },
  mikrotikWireless: {
    modeloPadrao: 'Mikrotik hAP ax2',
    quantidadePadrao: 1,
    valorMensalPadrao: 219,
    precoVendaPadrao: 3500,
    pctHardwarePadrao: 70,
  },
  accessPoint: {
    modeloPadrao: 'UniFi U7 Pro',
    quantidadePadrao: 1,
    valorMensalPadrao: 110,
    precoVendaPadrao: 1800,
    pctHardwarePadrao: 70,
  },
  switch: {
    modeloPadrao: 'USW-Pro-Max-16-PoE',
    quantidadePadrao: 1,
    valorMensalPadrao: 280,
    precoVendaPadrao: 5200,
    pctHardwarePadrao: 70,
  },
  nobreak: {
    modeloPadrao: 'Intelbras Compact Plus 4',
    quantidadePadrao: 1,
    valorMensalPadrao: 85,
    precoVendaPadrao: 1400,
    pctHardwarePadrao: 60,
  },
};

const precosHaasSchema = z.object({
  mikrotik: precosHaasItemSchema,
  mikrotikWireless: precosHaasItemSchema,
  accessPoint: precosHaasItemSchema,
  switch: precosHaasItemSchema,
  nobreak: precosHaasItemSchema,
});

// ─── Modulos Premium (Contrato v2026.08, clausulas 2.5/2.6) ────────────────
const precosPremiumModuloSchema = z.object({
  id: z.string().min(1).max(40),
  nome: z.string().min(1).max(120),
  escopoResumido: z.string().min(1).max(500),
  valorHoraPadrao: rsSchema,
  ativo: z.boolean().default(true),
});

const precosPremiumModulosDefault = {
  itens: [
    {
      id: 'infra-programada',
      nome: 'Infraestrutura Programada',
      escopoResumido:
        '3h mensais de atividades presenciais programadas de infraestrutura (preventivas, organizacao de rack, verificacoes fisicas). Nao cumulativas.',
      valorHoraPadrao: 0,
      ativo: true,
    },
    {
      id: 'governanca-ti',
      nome: 'Governanca de TI',
      escopoResumido:
        'Reuniao mensal de governanca (videoconferencia ou presencial), revisao dos indicadores do relatorio, plano de acao acompanhado e roadmap de TI.',
      valorHoraPadrao: 0,
      ativo: true,
    },
    {
      id: 'seguranca-avancada',
      nome: 'Seguranca Avancada',
      escopoResumido:
        'Hardening de endpoints, revisao trimestral de acessos e permissoes, campanha semestral de conscientizacao/phishing.',
      valorHoraPadrao: 0,
      ativo: true,
    },
  ],
  comboPct: 10,
};

const precosPremiumModulosSchema = z.object({
  itens: z.array(precosPremiumModuloSchema),
  comboPct: z.number().int().min(0).max(100).default(10),
});

export const precosVendaSchema = z.object({
  essencial: precosEssencialSchema,
  professional: precosProfessionalSchema,
  premium: precosPremiumSchema,
  emailImap: precosEmailImapSchema,
  emailExchange: precosEmailExchangeSchema,
  emailExtras: precosEmailExtrasSchema,
  backupNuvem: precosBackupNuvemSchema,
  redeWireless: precosRedeWirelessSchema,
  firewall: precosFirewallSchema,
  seguranca: precosSegurancaSchema,
  dispositivos: precosDispositivosSchema,
  onboarding: precosOnboardingSchema,
  haas: precosHaasSchema.default(precosHaasDefault),
  premiumModulos: precosPremiumModulosSchema.default(precosPremiumModulosDefault),
});

// ─── Bundles ─────────────────────────────────────────────────────────────

const bundleItemInclusoSchema = z.object({
  id: z.string().min(1).max(80),
  label: z.string().min(1).max(160),
  descricao: z.string().max(500).optional(),
});

const bundleItemCobradoSchema = z.object({
  id: z.string().min(1).max(80),
  label: z.string().min(1).max(160),
  unidade: z.enum(['dispositivo', 'usuario', 'servidor', 'mes', 'hora', 'unidade']),
  valor: rsSchema,
  aplicaMultiplicador: z.boolean(),
});

export const bundleSchema = z.object({
  nome: z.string().min(1).max(80),
  slogan: z.string().min(1).max(200),
  descricao: z.string().min(1).max(2000),
  destaque: z.string().max(40).optional(),
  inclusos: z.array(bundleItemInclusoSchema).max(50),
  cobrados: z.array(bundleItemCobradoSchema).max(50),
});

export const bundlesSchema = z.object({
  essencial: bundleSchema,
  professional: bundleSchema,
  premium: bundleSchema,
});

// ─── Perfil ──────────────────────────────────────────────────────────────

const multiplicadoresSchema = z.object({
  simples: z.number().min(1).max(3),
  moderado: z.number().min(1).max(3),
  critico: z.number().min(1).max(3),
});

const perfilOnboardingValor = z.enum(['sem', 'express', 'standard', 'enterprise']);
const onboardingPorPerfilSchema = z.object({
  simples: perfilOnboardingValor,
  moderado: perfilOnboardingValor,
  critico: perfilOnboardingValor,
});

// ─── Custos internos ─────────────────────────────────────────────────────

const custosPlataformasSchema = z.object({
  rmm: rsSchema,
  antivirus: rsSchema,
  edr: rsSchema,
  mikrotikCapexAmort: rsSchema,
});

const custosBackupCoveSchema = z.object({
  servidor: rsSchema,
  vm: rsSchema,
  estacao: rsSchema,
  perGb: rsSchema,
  m365User: rsSchema,
});

const custosMaoDeObraSchema = z.object({
  haloPsaTecnicoMes: rsSchema,
  n1CustoHora: rsSchema,
  n2CustoHora: rsSchema,
  gestaoTecnicoMes: rsSchema,
  comercialContratoMes: rsSchema,
});

const custosEstruturaSchema = z.object({
  overheadPct: pctSchema,
  impostosPct: pctSchema,
  contratosAtivos: z.number().int().nonnegative(),
});

export const custosInternosSchema = z.object({
  dataKrt: z.string().regex(/^\d{4}-\d{2}$/, 'Formato esperado: AAAA-MM'),
  usdBrl: rsSchema,
  plataformas: custosPlataformasSchema,
  backupCove: custosBackupCoveSchema,
  maoDeObra: custosMaoDeObraSchema,
  estrutura: custosEstruturaSchema,
});

// ─── Renovação (#219) — config de auto-aprovação ───────────────────────

export const renovacaoConfigSchema = z.object({
  ipcaTetoAprovacao: pctSchema,
  descontoMaxSemAprovacao: pctSchema,
  ipcaAtualizadoEm: z
    .string()
    .regex(/^\d{4}-\d{2}$/, 'Formato esperado: AAAA-MM')
    .optional(),
  fonteIpca: z.string().max(200).optional(),
});

// ─── Documento raiz ──────────────────────────────────────────────────────

export const tabelaPrecosCompletaSchema = z.object({
  id: z.string().min(1),
  tipo: z.enum(['atual', 'historico']),
  versao: z.number().int().positive(),
  vigenteDesde: isoTimestampSchema,
  alteradoPor: emailSchema,
  venda: precosVendaSchema,
  bundles: bundlesSchema,
  multiplicadores: multiplicadoresSchema,
  onboardingPorPerfil: onboardingPorPerfilSchema,
  custosInternos: custosInternosSchema,
  renovacaoConfig: renovacaoConfigSchema.optional(),
  _v: z.number().int().positive(),
  _ts: z.number().optional(),
});

/** Schema do payload de save (sem campos de metadata gerados pelo servidor). */
export const tabelaPrecosInputSchema = z.object({
  venda: precosVendaSchema,
  bundles: bundlesSchema,
  multiplicadores: multiplicadoresSchema,
  onboardingPorPerfil: onboardingPorPerfilSchema,
  custosInternos: custosInternosSchema,
  renovacaoConfig: renovacaoConfigSchema.optional(),
  alteradoPor: emailSchema,
});

export type TabelaPrecosInputSchema = z.infer<typeof tabelaPrecosInputSchema>;
